import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';

import { GreyText, MessageBox, RedText } from './TextComponents';
import { RecommendedFix } from 'src/pages/events/error/recommended-fix/RecommendedFix';
import { getUserImpactFromError } from 'src/pages/events/error/utils';
import {
  ErrorPreviewLogProps,
  PreviewLogErrorSeverity,
} from 'src/slices/workflow-preview/types';
import { ErrorQueryEvent } from 'src/types/ErrorEventTypes';

/**
 * `as const` rather than Record<..., string>: the i18n `t` is typed against the
 * key catalog, so a widened string here fails to type-check at the call site.
 */
const SEVERITY_LABEL_KEYS = {
  high: 'previewLogs.error.severityHigh',
  low: 'previewLogs.error.severityLow',
  medium: 'previewLogs.error.severityMedium',
} as const satisfies Record<PreviewLogErrorSeverity, string>;

/**
 * The Error Logs row this preview error would be. RecommendedFix and the user
 * impact helper key off a row; the preview log is the same recorded event
 * without the row's bookkeeping fields. A preview conversation stands in for
 * the widget, so that is the channel its fix and deep link resolve against.
 *
 * error_id, org_id, trace_id, timestamp and conversation_id are placeholders:
 * a preview log has none of them, and neither consumer reads them today. If
 * either starts to, it needs real values here or it will render these blanks.
 */
const toErrorQueryEvent = (
  previewLog: ErrorPreviewLogProps,
): ErrorQueryEvent => ({
  channel: 'widget',
  conversation_id: null,
  error_id: '',
  error_message: previewLog.error_message,
  error_payload: previewLog.error_payload ?? {},
  error_type: previewLog.error_type ?? null,
  org_id: '',
  product: 'Solve Widget',
  severity: previewLog.severity ?? null,
  status_code: previewLog.status_code ?? null,
  timestamp: '',
  trace_id: '',
});

// Same isolation as the fix section below: a helper written for Error Logs rows
// tripping on a preview log loses the line, not the entry.
const getPreviewUserImpact = (
  errorEvent: ErrorQueryEvent,
): string | undefined => {
  try {
    return getUserImpactFromError(errorEvent);
  } catch {
    return undefined;
  }
};

/**
 * Renders an error the runtime recorded during this preview conversation.
 *
 * These are the errors the customer's Error Logs / API Error Logs pages show
 * (the server mirrors only those types), with the same user impact and "How
 * to fix" guidance, so a builder can act on a failure where they are working
 * instead of finding it on the dashboard later. That covers fatal ones, where
 * the turn fails and the widget recovers to the previous step, and the
 * non-fatal ones the runtime swallows and continues past.
 */
export const ErrorPreviewLog = ({
  previewLog,
}: {
  previewLog: ErrorPreviewLogProps;
}) => {
  const { t } = useTranslation('workflow-builder-preview');
  const { t: tEvents } = useTranslation('events');
  const {
    error_message: errorMessage,
    error_payload: errorPayload,
    error_type: errorType,
    severity,
    status_code: statusCode,
  } = previewLog;

  const severityLabelKey = severity ? SEVERITY_LABEL_KEYS[severity] : undefined;
  const payloadEntries = Object.entries(errorPayload ?? {});
  const errorEvent = toErrorQueryEvent(previewLog);
  const userImpact = getPreviewUserImpact(errorEvent);
  const hasUserImpact =
    Boolean(userImpact) && userImpact !== tEvents('errorDrawer.notApplicable');

  return (
    <Box display='flex' flexDirection='column'>
      <span>
        <RedText>{t('previewLogs.error.label')}</RedText>
        <RedText>{errorMessage}</RedText>
      </span>
      {severityLabelKey && (
        <GreyText>
          {t('previewLogs.error.severity', { severity: t(severityLabelKey) })}
        </GreyText>
      )}
      {hasUserImpact && (
        <GreyText>{t('previewLogs.error.userImpact', { userImpact })}</GreyText>
      )}
      {errorType && (
        <GreyText>{t('previewLogs.error.errorType', { errorType })}</GreyText>
      )}
      {/* Explicit null check: a 0 status code is meaningless but an
          intentionally-raised error carries none at all, and "500" is worth
          distinguishing from "no response". */}
      {statusCode != null && (
        <GreyText>{t('previewLogs.error.statusCode', { statusCode })}</GreyText>
      )}
      {payloadEntries.length > 0 && (
        <MessageBox>{JSON.stringify(errorPayload, null, 2)}</MessageBox>
      )}
      {/* The fix renderers were written against Error Logs rows; if one ever
          trips on a payload shape only preview produces, lose the guidance,
          not the whole console. */}
      <Sentry.ErrorBoundary fallback={<></>}>
        <Box mt={1}>
          <RecommendedFix error={errorEvent} mode='compact' surface='console' />
        </Box>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
